<template>
  <div
    class="lesson-card"
    @click="$emit('card-clicked')"
    role="button"
    tabindex="0"
    @keydown.enter="$emit('card-clicked')"
    @keydown.space.prevent="$emit('card-clicked')"
  >
    <div class="card-content">
      <div class="progress-badge" v-if="isLogged">
        <Badge
          :label="`${lesson.progress || 0}%`"
          :variant="(lesson.progress || 0) > 0 ? 'pink-dark' : 'yellow-light'"
        />
      </div>

      <div class="like-icon-container" v-if="canLike">
        <img
          v-if="isLiked && isLogged"
          height="40"
          src="/images/liked-01.svg"
          alt="Remove from favourites"
          class="like-icon"
          @click.stop="handleLikeClick"
          @keydown.enter.stop="handleLikeClick"
          @keydown.space.prevent.stop="handleLikeClick"
          role="button"
          tabindex="0"
        />
        <img
          v-else-if="!isLiked && isLogged"
          height="40"
          src="/images/like-01.svg"
          alt="Add to favourites"
          class="like-icon"
          @click.stop="handleLikeClick"
          @keydown.enter.stop="handleLikeClick"
          @keydown.space.prevent.stop="handleLikeClick"
          role="button"
          tabindex="0"
        />
        <img
          v-else
          height="30"
          src="/images/like-brown.svg"
          alt=""
          class="like-icon"
          style="cursor: not-allowed"
        />
      </div>

      <div
        class="card-image"
        :style="`background-image: url('${
          lesson.thumbnail || '/images/placeholder-lesson.jpg'
        }')`"
      >
        <div class="card-overlay"></div>
      </div>

      <div class="item-info-container">
        <div class="item-info">
          <div class="title-section">
            {{ lesson.title }}
          </div>
        </div>

        <div class="line-breaker"></div>

        <p class="card-description">
          {{ lesson.short_description }}
        </p>

        <div class="grade-info" v-if="formattedGrades">
          <span class="grade-label">Grade:</span>
          <span class="grade-value">{{ formattedGrades }}</span>
        </div>

        <div class="subject-duration-row">
          <div class="subject-info" v-if="formattedSubjects">
            <span class="subject-label">Subject:</span>
            <span class="subject-value">{{ formattedSubjects }}</span>
          </div>
          <div class="duration-info">
            <span class="duration-label">Duration:</span>
            <span class="duration-value"
              >{{ lesson.duration || 0 }} Minutes</span
            >
          </div>
        </div>

        <div class="tags-section" v-if="domainTags.length">
          <div
            v-for="(domain, index) in domainTags"
            :key="`domain-${index}`"
            class="item-tag"
          >
            {{ domain }}
          </div>
          <div v-if="remainingDomainCount > 0" class="more-tags">
            {{ remainingDomainCount }}+ more
          </div>
        </div>

        <div class="tags-section" v-if="softwareTags.length">
          <div
            v-for="(software, index) in softwareTags"
            :key="`software-${index}`"
            class="item-tag"
          >
            {{ software }}
          </div>
          <div v-if="remainingSoftwareCount > 0" class="more-tags">
            {{ remainingSoftwareCount }}+ more
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, defineEmits, defineProps, ref, watch } from "vue";
import professionalDevelopmentService from "@/services/professionalDevelopmentService";
import Badge from "@/components/common/Badge.vue";

const props = defineProps({
  lesson: {
    type: Object,
    required: true,
  },
  isLogged: {
    type: Boolean,
    default: false,
  },
  canLike: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(["card-clicked", "update-liked-status"]);

const isLiked = ref(props.lesson.isLiked || false);
const likeCount = ref(props.lesson.like_count || 0);

watch(
  () => props.lesson.isLiked,
  (newValue) => {
    isLiked.value = newValue || false;
  }
);

watch(
  () => props.lesson.like_count,
  (newValue) => {
    likeCount.value = newValue || 0;
  }
);

const formattedGrades = computed(() => {
  return props.lesson.grade?.map((grade) => grade.title).join(", ") || "";
});

const formattedSubjects = computed(() => {
  return props.lesson.subject?.map((subject) => subject.title).join(", ") || "";
});

const domainTags = computed(() => {
  return props.lesson.domain?.slice(0, 3).map((domain) => domain.title) || [];
});

const remainingDomainCount = computed(() => {
  const totalDomains = props.lesson.domain?.length || 0;
  return totalDomains > 3 ? totalDomains - 3 : 0;
});

const softwareTags = computed(() => {
  return (
    props.lesson.software?.slice(0, 3).map((software) => software.title) || []
  );
});

const remainingSoftwareCount = computed(() => {
  const totalSoftware = props.lesson.software?.length || 0;
  return totalSoftware > 3 ? totalSoftware - 3 : 0;
});

const handleLikeClick = async () => {
  if (!props.isLogged) {
    return;
  }

  const previousLikedState = isLiked.value;
  const previousLikeCount = likeCount.value;

  isLiked.value = !isLiked.value;
  likeCount.value = previousLikedState
    ? Math.max(previousLikeCount - 1, 0)
    : previousLikeCount + 1;

  try {
    const response = await professionalDevelopmentService.likeLesson(
      props.lesson.id
    );

    if (response.status !== 200) {
      isLiked.value = previousLikedState;
      likeCount.value = previousLikeCount;
      console.error("API error liking lesson:", response);
      return;
    }

    emit("update-liked-status", {
      id: props.lesson.id,
      liked: isLiked.value,
      likeCount: likeCount.value,
    });
  } catch (error) {
    isLiked.value = previousLikedState;
    likeCount.value = previousLikeCount;
    console.error("Error liking lesson:", error);
  }
};
</script>

<style scoped>
.lesson-card {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  height: 450px;
  position: relative;
  cursor: pointer;
  transition: transform 0.3s ease;
}

.lesson-card:hover {
  transform: translateY(-5px);
}

.card-content {
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.card-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  z-index: 1;
}

.card-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.6);
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 2;
}

.lesson-card:hover .card-overlay {
  opacity: 1;
}

.progress-badge {
  position: absolute;
  top: 1rem;
  left: 1rem;
  z-index: 10;
}

.like-icon-container {
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 10;
}

.like-icon {
  cursor: pointer;
  transition: transform 0.2s ease-in-out;
}

.like-icon:hover {
  transform: scale(1.1);
  filter: brightness(0.9);
}

.item-info-container {
  position: absolute;
  text-align: left;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 1rem;
  padding-bottom: 40px;
  height: 250px;
  z-index: 3;
  transform: translateY(calc(100% - 110px));
  transition:
    transform 0.4s ease,
    padding-bottom 0.4s ease;
  background-color: #f2f3fb;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  border-radius: 0 0 12px 12px;
}

.item-info-container::after {
  content: "";
  position: absolute;
  top: -25px;
  left: -25px;
  right: -25px;
  height: 20%;
  background-color: #f2f3fb;
  opacity: 0;
  filter: blur(8px);
  z-index: 5;
  transition: opacity 0.3s ease;
}

.lesson-card:hover .item-info-container {
  transform: translateY(0px);
  padding-bottom: 1rem;
}

.lesson-card:hover .item-info-container::after {
  opacity: 1;
}

.line-breaker {
  width: 100%;
  height: 1px;
  border-bottom: 2px dashed #98a2b3;
  margin: 4px 0;
  opacity: 0;
}

.lesson-card:hover .line-breaker {
  opacity: 1;
}

.item-info {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 0;
  z-index: 6;
}

.title-section {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-xxl);
  font-weight: 700;
  line-height: 1.4;
  color: var(--primary-blue);
  text-align: left;
  word-break: break-word;
  flex: 1;
  margin-right: 0.5rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.card-description {
  font-family: "League Spartan", sans-serif;
  font-size: 0.875rem;
  line-height: 1.4;
  color: #40444b;
  margin-top: -0.25rem;
  margin-bottom: -0.25rem;
  opacity: 0;
  transform: translateY(20px);
  transition:
    opacity 0.4s ease,
    transform 0.4s ease;
  transition-delay: 0.1s;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.lesson-card:hover .card-description {
  opacity: 1;
  transform: translateY(0);
}

.grade-info {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  font-family: "League Spartan", sans-serif;
  font-size: 0.875rem;
  line-height: 1.25;
  opacity: 0;
  transform: translateY(20px);
  transition:
    opacity 0.4s ease,
    transform 0.4s ease;
  transition-delay: 0.2s;
}

.lesson-card:hover .grade-info {
  opacity: 1;
  transform: translateY(0);
}

.grade-label {
  font-weight: 600;
  color: #40444b;
}

.grade-value {
  font-weight: 400;
  color: #3b4648;
}

.subject-duration-row {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  font-family: "League Spartan", sans-serif;
  font-size: 0.875rem;
  line-height: 1.25;
  opacity: 0;
  transform: translateY(20px);
  transition:
    opacity 0.4s ease,
    transform 0.4s ease;
  transition-delay: 0.3s;
}

.lesson-card:hover .subject-duration-row {
  opacity: 1;
  transform: translateY(0);
}

.subject-info,
.duration-info {
  display: flex;
  gap: 0.25rem;
}

.subject-label,
.duration-label {
  font-weight: 600;
  color: #40444b;
}

.subject-value,
.duration-value {
  font-weight: 400;
  color: #3b4648;
}

.tags-section {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.25rem;
  opacity: 0;
  transform: translateY(20px);
  transition:
    opacity 0.4s ease,
    transform 0.4s ease;
  transition-delay: 0.4s;
}

.lesson-card:hover .tags-section {
  opacity: 1;
  transform: translateY(0);
}

.item-tag {
  background-color: #ffffff;
  border-radius: 4px;
  padding: 0.25rem 0.75rem;
  font-family: "League Spartan", sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
  color: #40444b;
  border: 1px solid var(--neutral-100);
  white-space: nowrap;
}

.more-tags {
  border-radius: 20px;
  padding: 0.25rem 0.75rem;
  font-family: "League Spartan", sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
  color: #3b4648;
  white-space: nowrap;
}

@media (max-width: 768px) {
  .lesson-card {
    height: 250px;
  }

  .item-info-container {
    z-index: 20;
    transform: translateY(calc(100% - 50px)) !important;
    padding-bottom: 40px !important;
    pointer-events: auto;
  }

  .title-section {
    -webkit-line-clamp: 1;
    line-clamp: 1;
    font-size: var(--fs-small);
  }

  .subject-duration-row {
    flex-direction: column;
    gap: 0;
  }

  .progress-badge {
    top: 0.75rem;
    left: 0.75rem;
  }

  .like-icon-container {
    top: 5px;
    right: 5px;
    z-index: 21;
  }

  .lesson-card:hover {
    transform: none;
  }

  .lesson-card:hover .card-overlay {
    opacity: 0;
  }

  .lesson-card:hover .line-breaker {
    opacity: 0;
  }

  .lesson-card:hover .grade-info {
    opacity: 0;
    transform: translateY(20px);
  }

  .lesson-card:hover .subject-duration-row {
    opacity: 0;
    transform: translateY(20px);
  }

  .lesson-card:hover .tags-section,
  .lesson-card:hover .card-description {
    opacity: 0;
    transform: translateY(20px);
  }

  .lesson-card:hover .item-info-container::after {
    opacity: 0;
  }
}
</style>
